import React, { Suspense } from 'react';

import Splash from '@/components/shared/Splash';
import AuthGuard from '@/components/shared/authGuard/AuthGuard';
import { IPFBRoute } from '@/types';

/**
 * Splits routes by authenticated and unauthenticated properties and processes
 * nested children routes recursively.
 *
 * @param {IPFBRoute[]} pfbRoutes - The array of routes to be processed.
 * @returns {{authRoutes: IPFBRouter[], unAuthRoutes: IPFBRouter[]}} An object
 * containing two arrays:one for authenticated routes and one for
 * unauthenticated routes.
 */

const getRouteType = (route: IPFBRoute): 'authRoutes' | 'unAuthRoutes' =>
	route.isAuthenticated ? 'authRoutes' : 'unAuthRoutes';

export const splitRoutesByAuth = (
	pfbRoutes: IPFBRoute[],
): { authRoutes: IPFBRoute[]; unAuthRoutes: IPFBRoute[] } => {
	return pfbRoutes.reduce(
		(acc, route) => {
			const preparedRoute = {
				...route,
				element: route.isAuthenticated ? (
					<Suspense fallback={<Splash />}>
						<AuthGuard {...route}>{route.element}</AuthGuard>
					</Suspense>
				) : (
					<Suspense fallback={<Splash />}>{route.element}</Suspense>
				),
				// Adding children to auth/unauth route from the returned object
				children: route.children
					? splitRoutesByAuth(route.children)[getRouteType(route)]
					: [],
			};

			if (route.isAuthenticated) {
				acc.authRoutes.push(preparedRoute);
			} else {
				acc.unAuthRoutes.push(preparedRoute);
			}

			return acc;
		},
		{ authRoutes: [], unAuthRoutes: [] } as {
			authRoutes: IPFBRoute[];
			unAuthRoutes: IPFBRoute[];
		},
	);
};
